/**
 * Hides parent elements when required child content is missing or empty.
 * Rules are hardcoded in `THEME_HIDE_EMPTY_PARENT_RULES` below.
 *
 * Each rule:
 * - parentSelector: element(s) to hide (each match evaluated separately).
 * - contentSelector: one child selector checked inside each parent, OR
 * - contentSelectors: string[] — hide only when none of these match (exists) / none have meaningful content (has-content).
 * - mode (optional): `exists` (default) or `has-content` (text or img/video/svg/etc.).
 */

/** @typedef {{ parentSelector: string, contentSelector?: string, contentSelectors?: string[], mode?: 'exists' | 'has-content' }} ThemeHideEmptyRule */

/**
 * Add entries here: unique key → { parentSelector, contentSelector | contentSelectors, mode? }.
 * @type {Record<string, ThemeHideEmptyRule>}
 */
const THEME_HIDE_EMPTY_PARENT_RULES = {
  // example:
  // productFaqsSection: {
  //   parentSelector: '#shopify-section-template--xxxx__main',
  //   contentSelector: '.product-faqs-informacion-adicional .accordion',
  //   mode: 'exists',
  // },
  productExtraInfoSection1: {
    parentSelector: '#product-extra-info-section-1',
    contentSelectors: ['.image-block'],
  }
};

(function hideEmptyParents() {
  /** @type {Window & { applyHideEmptyParents?: (scope?: Document | HTMLElement) => void }} */
  const win = window;

  const HIDDEN_ATTR = 'hidden';
  const MARK = 'data-hide-empty-parents';

  /**
   * @param {ThemeHideEmptyRule} rule
   * @returns {string[]}
   */
  function selectorsForRule(rule) {
    if (Array.isArray(rule.contentSelectors) && rule.contentSelectors.length > 0) {
      return rule.contentSelectors;
    }
    if (rule.contentSelector) {
      return [rule.contentSelector];
    }
    return [];
  }

  /**
   * @param {ParentNode} scope
   * @param {string} selector
   * @returns {Element[]}
   */
  function queryAll(scope, selector) {
    try {
      return [...scope.querySelectorAll(selector)];
    } catch {
      return [];
    }
  }

  /**
   * @param {ThemeHideEmptyRule} rule
   * @param {Document | HTMLElement} scope
   * @returns {HTMLElement[]}
   */
  function parentsForRule(rule, scope) {
    if (scope instanceof Document) {
      return queryAll(scope, rule.parentSelector).filter((el) => el instanceof HTMLElement);
    }
    if (!(scope instanceof HTMLElement)) {
      return [];
    }
    if (scope.matches(rule.parentSelector)) {
      return [scope];
    }
    return queryAll(scope, rule.parentSelector).filter((el) => el instanceof HTMLElement);
  }

  /**
   * @param {Element} el
   * @returns {boolean}
   */
  function hasMeaningfulContent(el) {
    const text = el.textContent?.trim() ?? '';
    if (text.length > 0) {
      return true;
    }
    return Boolean(el.querySelector('img, picture, video, iframe, svg, canvas'));
  }

  /**
   * @param {HTMLElement} parent
   * @param {ThemeHideEmptyRule} rule
   * @returns {boolean}
   */
  function shouldHideParent(parent, rule) {
    const selectors = selectorsForRule(rule);
    if (selectors.length === 0) {
      return false;
    }

    const mode = rule.mode ?? 'exists';

    for (const sel of selectors) {
      let el = null;
      try {
        el = parent.querySelector(sel);
      } catch {
        continue;
      }
      if (!el) {
        continue;
      }
      if (mode === 'exists') {
        return false;
      }
      if (hasMeaningfulContent(el)) {
        return false;
      }
    }

    return true;
  }

  /**
   * @param {HTMLElement} parent
   * @param {boolean} hide
   */
  function setHidden(parent, hide) {
    if (hide) {
      parent.setAttribute(HIDDEN_ATTR, '');
      parent.setAttribute(MARK, 'true');
      return;
    }
    if (parent.getAttribute(MARK) === 'true') {
      parent.removeAttribute(HIDDEN_ATTR);
      parent.removeAttribute(MARK);
    }
  }

  /**
   * @param {Document | HTMLElement} [scope]
   */
  function applyHideEmptyParents(scope = document) {
    const entries = Object.values(THEME_HIDE_EMPTY_PARENT_RULES);
    if (entries.length === 0) {
      return;
    }

    /** @type {Map<HTMLElement, boolean>} */
    const hideByParent = new Map();

    for (const rule of entries) {
      if (!rule.parentSelector) {
        continue;
      }

      const parents = parentsForRule(rule, scope);
      for (const parent of parents) {
        const hide = shouldHideParent(parent, rule);
        hideByParent.set(parent, Boolean(hideByParent.get(parent)) || hide);
      }
    }

    for (const [parent, hide] of hideByParent) {
      setHidden(parent, hide);
    }
  }

  win.applyHideEmptyParents = applyHideEmptyParents;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => applyHideEmptyParents(document), { once: true });
  } else {
    applyHideEmptyParents(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof HTMLElement) {
      applyHideEmptyParents(event.target);
    }
  });
})();
